import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, useEditorState,
  type Editor, type NodeViewProps,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Placeholder } from '@tiptap/extensions'
import {
  Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Code, SquareCode, Link as LinkIcon, Minus, Undo2, Redo2, type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { cn } from '@/lib/utils'
import './RichTextEditor.css'

/**
 * Rich-text editor that stores HTML renderable by the app (flutter_html).
 * Drop-in replacement for a Textarea: `value` in, `onChange` with HTML out.
 * The supported formatting is documented in the ticket — keep the toolbar in sync.
 */
export function RichTextEditor({
                                 value,
                                 onChange,
                                 placeholder,
                                 id,
                                 className,
                                 contentClassName,
                               }: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  id?: string
  /** Classes for the outer frame (toolbar + content), e.g. flex sizing. */
  className?: string
  /** Classes for the editable area itself, e.g. a larger min height. */
  contentClassName?: string
}) {
  // Ref so the editor (created once) always calls the latest onChange.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // HTML last emitted by the editor itself — echoes of our own updates are ignored.
  const lastEmittedRef = useRef(value)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
      }),
      InlineImage,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: toEditorHtml(value),
    onUpdate: ({ editor }) => {
      const html = fromEditor(editor)
      lastEmittedRef.current = html
      onChangeRef.current(html)
    },
    editorProps: {
      attributes: {
        class: cn('rich-text-editor__content', contentClassName),
        ...(id ? { id } : {}),
      },
    },
  })

  // Sync only genuinely external value changes (e.g. module loaded asynchronously).
  // Not added to the undo history — otherwise undo would revert the initial load.
  useEffect(() => {
    if (!editor || value === lastEmittedRef.current) return
    lastEmittedRef.current = value
    editor
      .chain()
      .setMeta('addToHistory', false)
      .setContent(toEditorHtml(value), { emitUpdate: false })
      .run()
  }, [editor, value])

  if (!editor) return null

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-md border bg-transparent shadow-xs focus-within:ring-2 focus-within:ring-ring/50',
        className,
      )}
    >
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

type ToolbarItem = {
  icon: LucideIcon
  label: string
  onClick: () => void
  active?: boolean
  disabled?: boolean
}

/** Formatting toolbar — only offers what the app (flutter_html) renders correctly. */
function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation()

  // Tiptap 3 doesn't re-render on every transaction — subscribe to exactly the
  // state the toolbar needs so active/disabled states stay current.
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      strike: editor.isActive('strike'),
      h2: editor.isActive('heading', { level: 2 }),
      h3: editor.isActive('heading', { level: 3 }),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      blockquote: editor.isActive('blockquote'),
      code: editor.isActive('code'),
      codeBlock: editor.isActive('codeBlock'),
      link: editor.isActive('link'),
      canUndo: editor.can().undo(),
      canRedo: editor.can().redo(),
    }),
  })

  function setLink() {
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt(t('richTextEditor.linkPrompt'), previous ?? 'https://')
    if (url === null) return
    const chain = editor.chain().focus().extendMarkRange('link')
    if (url.trim() === '') chain.unsetLink().run()
    else chain.setLink({ href: url.trim() }).run()
  }

  const chain = () => editor.chain().focus()

  const groups: ToolbarItem[][] = [
    [
      { icon: Bold, label: t('richTextEditor.bold'), active: state.bold, onClick: () => chain().toggleBold().run() },
      { icon: Italic, label: t('richTextEditor.italic'), active: state.italic, onClick: () => chain().toggleItalic().run() },
      { icon: Underline, label: t('richTextEditor.underline'), active: state.underline, onClick: () => chain().toggleUnderline().run() },
      { icon: Strikethrough, label: t('richTextEditor.strikethrough'), active: state.strike, onClick: () => chain().toggleStrike().run() },
    ],
    [
      { icon: Heading2, label: t('richTextEditor.heading2'), active: state.h2, onClick: () => chain().toggleHeading({ level: 2 }).run() },
      { icon: Heading3, label: t('richTextEditor.heading3'), active: state.h3, onClick: () => chain().toggleHeading({ level: 3 }).run() },
    ],
    [
      { icon: List, label: t('richTextEditor.bulletList'), active: state.bulletList, onClick: () => chain().toggleBulletList().run() },
      { icon: ListOrdered, label: t('richTextEditor.orderedList'), active: state.orderedList, onClick: () => chain().toggleOrderedList().run() },
      { icon: Quote, label: t('richTextEditor.blockquote'), active: state.blockquote, onClick: () => chain().toggleBlockquote().run() },
    ],
    [
      { icon: Code, label: t('richTextEditor.code'), active: state.code, onClick: () => chain().toggleCode().run() },
      { icon: SquareCode, label: t('richTextEditor.codeBlock'), active: state.codeBlock, onClick: () => chain().toggleCodeBlock().run() },
      { icon: LinkIcon, label: t('richTextEditor.link'), active: state.link, onClick: setLink },
      { icon: Minus, label: t('richTextEditor.horizontalRule'), onClick: () => chain().setHorizontalRule().run() },
    ],
    [
      { icon: Undo2, label: t('richTextEditor.undo'), disabled: !state.canUndo, onClick: () => chain().undo().run() },
      { icon: Redo2, label: t('richTextEditor.redo'), disabled: !state.canRedo, onClick: () => chain().redo().run() },
    ],
  ]

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 p-1">
      {groups.map((group, groupIndex) => (
        <div key={groupIndex} className="flex items-center gap-0.5">
          {groupIndex > 0 && <Separator orientation="vertical" className="mx-1 h-5" />}
          {group.map((item) => (
            <Button
              key={item.label}
              type="button"
              variant="ghost"
              size="icon"
              className={cn('size-7', item.active && 'bg-muted text-foreground')}
              title={item.label}
              aria-label={item.label}
              aria-pressed={item.active}
              disabled={item.disabled}
              // Keep the text selection when clicking a toolbar button.
              onMouseDown={(e) => e.preventDefault()}
              onClick={item.onClick}
            >
              <item.icon className="size-4" />
            </Button>
          ))}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline image
// ---------------------------------------------------------------------------

/**
 * Inline images (existing descriptions place them directly in the text), resizable via
 * a custom node view. Keeps width/height/style so existing HTML survives a round trip.
 * Uploading new images is out of scope here (follow-up ticket).
 */
const InlineImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      height: { default: null },
      style: { default: null },
    }
  },
  addNodeView() {
    return ReactNodeViewRenderer(InlineImageView)
  },
}).configure({ inline: true })

const MIN_IMAGE_WIDTH = 40

/**
 * Image with a resize handle (bottom right). While dragging, the width is only shown
 * locally; it is committed once on release — one undo step, one onChange.
 * Height is cleared on resize so the image scales proportionally in both editor and app.
 */
function InlineImageView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [dragWidth, setDragWidth] = useState<number | null>(null)

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    const img = imgRef.current
    if (!img) return
    e.preventDefault()
    e.stopPropagation()

    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)

    const startX = e.clientX
    const startWidth = img.offsetWidth
    const maxWidth = contentWidth(editor.view.dom as HTMLElement)
    let currentWidth = startWidth

    const onMove = (ev: PointerEvent) => {
      const next = startWidth + (ev.clientX - startX)
      currentWidth = Math.round(Math.min(maxWidth, Math.max(MIN_IMAGE_WIDTH, next)))
      setDragWidth(currentWidth)
    }
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)
      setDragWidth(null)
      // Commit only if the width actually changed — keeps the undo stack clean.
      if (currentWidth !== startWidth) {
        updateAttributes({ width: currentWidth, height: null, style: stripSizeFromStyle(node.attrs.style) })
      }
    }

    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }, [editor, updateAttributes, node.attrs.style])

  const width = dragWidth ?? node.attrs.width ?? undefined
  // Ignore stored height while dragging so the live preview stays proportional.
  const height = dragWidth ? undefined : node.attrs.height ?? undefined

  return (
    <NodeViewWrapper as="span" className="rich-text-editor__image" data-selected={selected || undefined}>
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt ?? ''}
        width={width}
        height={height}
        // Let ProseMirror handle dragging (move the node instead of the browser's native image copy).
        draggable
        data-drag-handle
      />
      {selected && (
        <span
          className="rich-text-editor__image-handle"
          draggable={false}
          onPointerDown={onPointerDown}
        />
      )}
    </NodeViewWrapper>
  )
}

/** Available width inside the editor content area (excluding padding). */
function contentWidth(el: HTMLElement): number {
  const style = getComputedStyle(el)
  return el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
}

/**
 * Removes width/height declarations from an inline style, keeping everything else.
 * Needed on resize: a leftover `style="width: …"` would override the new width
 * attribute in the app, because CSS takes precedence over HTML attributes.
 */
function stripSizeFromStyle(style: string | null): string | null {
  if (!style) return null
  const rest = style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !/^(width|height)\s*:/i.test(part))
    .join('; ')
  return rest || null
}

// ---------------------------------------------------------------------------
// HTML conversion
// ---------------------------------------------------------------------------

const HTML_TAG = /<\/?[a-z][\s\S]*?>/i

/**
 * Prepares a stored value for the editor. Existing values may be plain text with line
 * breaks — those become paragraphs so they don't collapse into one block. HTML is
 * passed through as-is.
 */
function toEditorHtml(value: string): string {
  if (!value) return ''
  if (HTML_TAG.test(value)) return value
  return value.split(/\r?\n/).map((line) => `<p>${escapeHtml(line)}</p>`).join('')
}

/** Current editor content as HTML; an empty editor yields '' instead of '<p></p>'. */
function fromEditor(editor: Editor): string {
  return editor.isEmpty ? '' : editor.getHTML()
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
